/**
 * Sophyteca - Admin
 * Página de administração para upload de livros
 * Data: 01 de Novembro de 2025
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function SophytecaAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    titulo: '',
    autor: '',
    editora: '',
    anoPublicacao: '',
    categoria: 'psicologia',
    descricao: '',
    palavrasChave: '',
    tags: '',
    destaque: false
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const ext = file.name.toLowerCase();
      
      if (!ext.endsWith('.pdf') && !ext.endsWith('.epub') && !ext.endsWith('.mobi')) {
        alert('Formato não suportado. Use PDF, EPUB ou MOBI');
        e.target.value = '';
        return;
      }
      
      if (file.size > 100 * 1024 * 1024) {
        alert('Arquivo muito grande. Tamanho máximo: 100MB');
        e.target.value = '';
        return;
      }
      
      setArquivo(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!arquivo) {
      alert('Selecione um arquivo');
      return;
    }

    if (!formData.titulo || !formData.autor) {
      alert('Preencha os campos obrigatórios: Título e Autor');
      return;
    }

    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('livro', arquivo);
      
      const metadados = {
        titulo: formData.titulo,
        autor: formData.autor,
        editora: formData.editora || undefined,
        anoPublicacao: formData.anoPublicacao ? parseInt(formData.anoPublicacao) : undefined,
        categoria: formData.categoria,
        descricao: formData.descricao || undefined,
        palavrasChave: formData.palavrasChave || undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        destaque: formData.destaque
      };
      
      formDataObj.append('metadados', JSON.stringify(metadados));

      const response = await api.post('/sophyteca/upload', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.sucesso) {
        alert('Livro cadastrado com sucesso!');
        navigate('/sophyteca');
      } else {
        alert('Erro: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[SOPHYTECA] Erro upload:', error);
      alert('Erro ao fazer upload: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-12">
              <h1>
                <i className="fas fa-upload text-primary"></i> Adicionar Livro
              </h1>
              <button 
                className="btn btn-outline-secondary mt-2"
                onClick={() => navigate('/sophyteca')}
              >
                <i className="fas fa-arrow-left"></i> Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card shadow">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Arquivo */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="sophytecaArquivo">
                    Arquivo * <small className="text-muted">(PDF, EPUB ou MOBI - Máx: 100MB)</small>
                  </label>
                  <input
                    id="sophytecaArquivo"
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                    accept=".pdf,.epub,.mobi"
                    required
                  />
                  {arquivo && (
                    <small className="text-success">
                      <i className="fas fa-check"></i> {arquivo.name} ({(arquivo.size / 1024 / 1024).toFixed(2)} MB)
                    </small>
                  )}
                </div>

                {/* Título */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="sophytecaTitulo">Título *</label>
                  <input
                    id="sophytecaTitulo"
                    type="text"
                    className="form-control"
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                  />
                </div>

                {/* Autor */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="sophytecaAutor">Autor *</label>
                  <input
                    id="sophytecaAutor"
                    type="text"
                    className="form-control"
                    value={formData.autor}
                    onChange={(e) => setFormData({ ...formData, autor: e.target.value })}
                    required
                  />
                </div>

                <div className="row">
                  {/* Editora */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="sophytecaEditora">Editora</label>
                    <input
                      id="sophytecaEditora"
                      type="text"
                      className="form-control"
                      value={formData.editora}
                      onChange={(e) => setFormData({ ...formData, editora: e.target.value })}
                    />
                  </div>

                  {/* Ano */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="sophytecaAno">Ano</label>
                    <input
                      id="sophytecaAno"
                      type="number"
                      className="form-control"
                      min="1500"
                      max={new Date().getFullYear()}
                      value={formData.anoPublicacao}
                      onChange={(e) => setFormData({ ...formData, anoPublicacao: e.target.value })}
                    />
                  </div>

                  {/* Categoria */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="sophytecaCategoria">Categoria *</label>
                    <select
                      id="sophytecaCategoria"
                      className="form-select"
                      value={formData.categoria}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      required
                    >
                      <option value="psicologia">Psicologia</option>
                      <option value="neurociencia">Neurociência</option>
                      <option value="filosofia">Filosofia</option>
                      <option value="saude_mental">Saúde Mental</option>
                      <option value="educacao">Educação</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                </div>

                {/* Descrição */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="sophytecaDescricao">Descrição</label>
                  <textarea
                    id="sophytecaDescricao"
                    className="form-control"
                    rows={4}
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Resumo ou sinopse do livro..."
                  />
                </div>

                {/* Palavras-chave */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="sophytecaPalavrasChave">
                    Palavras-chave <small className="text-muted">(para facilitar a busca)</small>
                  </label>
                  <input
                    id="sophytecaPalavrasChave"
                    type="text"
                    className="form-control"
                    value={formData.palavrasChave}
                    onChange={(e) => setFormData({ ...formData, palavrasChave: e.target.value })}
                    placeholder="Ex: terapia cognitiva comportamental, ansiedade, depressão"
                  />
                </div>

                {/* Tags */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="sophytecaTags">
                    Tags <small className="text-muted">(separadas por vírgula)</small>
                  </label>
                  <input
                    id="sophytecaTags"
                    type="text"
                    className="form-control"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="Ex: TCC, ansiedade, mindfulness"
                  />
                </div>

                {/* Destaque */}
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="destaque"
                    checked={formData.destaque}
                    onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="destaque">
                    Marcar como destaque (aparecerá no topo das buscas)
                  </label>
                </div>

                {/* Botões */}
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/sophyteca')}
                    disabled={loading}
                  >
                    <i className="fas fa-times"></i> Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i> Cadastrar Livro
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
